'use client';

import { useEffect, useState, type FormEvent } from 'react';

import { fieldClass } from '@/components/platform/agency-fields';
import { hostHref, shortDate } from '@/components/platform/format';
import { notify } from '@/components/super-admin/ui';
import { api } from '@/lib/api';
import { accountError, leaveIfSignedOut } from '@/lib/platform/accounts';
import { parseDomainCheck, parseTenantDomain, type DomainCheck, type TenantDomain } from '@/lib/platform/domain';

export function DomainTab({
  slug,
  publishedAt,
}: {
  slug: string;
  publishedAt: string | null;
}) {
  const [domain, setDomain] = useState<TenantDomain | null>(null);
  const [draft, setDraft] = useState('');
  const [check, setCheck] = useState<DomainCheck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api<unknown>(`/api/platform/tenants/${slug}/domain`)
      .then((payload) => {
        if (cancelled) return;
        const next = parseTenantDomain(payload);
        setDomain(next);
        setDraft(next.customDomain ?? '');
      })
      .catch((err: unknown) => {
        if (!cancelled && !leaveIfSignedOut(err)) setError(accountError(err, 'Could not load the domain.'));
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  async function persist(customDomain: string | null) {
    setSaving(true);
    setError(null);
    setCheck(null);
    try {
      const saved = parseTenantDomain(await api<unknown>(`/api/platform/tenants/${slug}/domain`, {
        method: 'PUT',
        body: JSON.stringify({ customDomain }),
      }));
      setDomain(saved);
      setDraft(saved.customDomain ?? '');
      notify(saved.customDomain ? 'Domain connected.' : 'Custom domain removed.');
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not save this domain.'));
    } finally {
      setSaving(false);
    }
  }

  function save(event: FormEvent) {
    event.preventDefault();
    const next = draft.trim();
    if (next === '') return;
    void persist(next);
  }

  async function verify() {
    setChecking(true);
    setError(null);
    try {
      setCheck(parseDomainCheck(await api<unknown>(`/api/platform/tenants/${slug}/domain/check`, { method: 'POST', body: '{}' })));
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not check this domain.'));
    } finally {
      setChecking(false);
    }
  }

  if (!domain) return <p className="text-sm text-muted">{error ?? 'Loading the address…'}</p>;

  const site = domain.customDomain ?? domain.subdomain;
  const busy = saving || checking;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-ink">Address</h3>
        <p className="mt-1 text-xs text-muted">The subdomain is created with the agency. A domain the agency already owns can point at it.</p>
      </div>
      <dl className="grid gap-3 sm:grid-cols-2">
        <div>
          <dt className="text-xs text-muted">Subdomain</dt>
          <dd className="text-sm text-ink">{domain.subdomain}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Storefront</dt>
          <dd className="text-sm text-ink">{publishedAt ? `Published ${shortDate(publishedAt)}` : 'Not published'}</dd>
        </div>
      </dl>
      <p className="text-xs text-muted">
        Open the site at <a className="font-semibold text-ink" href={hostHref(site)}>{site}</a>
      </p>
      <form onSubmit={(event) => void save(event)} className="space-y-3 border-t border-line pt-4">
        <label className="block text-xs font-medium text-muted">
          Custom domain
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="rentals.example.com"
            disabled={!domain.customDomainAllowed}
            className={fieldClass}
          />
        </label>
        {domain.customDomainAllowed ? (
          <p className="text-xs text-muted">
            Add a CNAME for that hostname to <span className="text-ink">{domain.target}</span>. An apex name such as example.com needs an ALIAS record, or an A record to this platform.
          </p>
        ) : (
          <p className="text-xs text-warning">This plan does not include a custom domain. The subdomain stays available.</p>
        )}
        {check && <p className={`text-xs font-semibold ${checkClass(check.status)}`}>{check.detail}</p>}
        {error && <p className="text-xs font-semibold text-danger">{error}</p>}
        <div className="flex flex-wrap gap-2">
          <button type="submit" disabled={busy || !domain.customDomainAllowed || draft.trim() === ''} className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950 disabled:opacity-50">
            {saving ? 'Saving…' : 'Connect domain'}
          </button>
          <button type="button" disabled={busy || domain.customDomain === null} onClick={() => void persist(null)} className="rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink disabled:opacity-50">
            Remove domain
          </button>
          <button type="button" disabled={busy || domain.customDomain === null} onClick={() => void verify()} className="rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink disabled:opacity-50">
            {checking ? 'Checking…' : 'Check DNS'}
          </button>
        </div>
      </form>
    </div>
  );
}

function checkClass(status: DomainCheck['status']): string {
  switch (status) {
    case 'connected':
      return 'text-success';
    case 'waiting':
      return 'text-warning';
    case 'mismatch':
      return 'text-danger';
    default: {
      const unknown: never = status;
      return unknown;
    }
  }
}
